import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { getOptimizedBase64FromInputFile } from '@utils/Base64';

interface PhotoUploaderProps {
    value?: string;
    onChange: (base64: string) => void;
}

// Uploader cuadrado con preview: optimiza la imagen (800px / JPEG) y entrega un
// data URI, que es lo que el `<img>` del preview necesita para pintarse sin haber
// subido nada.
//
// ⚠️ Ese data URI **no llega a Firestore**: `DishServ.saveDish` lo sube a Storage
// y guarda la URL (ver `api/Catalog/CatalogPhotos.ts`). La subida ocurre al
// guardar, no aquí, para no dejar archivos huérfanos si se cancela el modal.
// Se sigue optimizando porque una foto de celular sin recomprimir son ~3 MB de
// tráfico por cada quien abra el menú.
export const PhotoUploader: React.FunctionComponent<PhotoUploaderProps> = ({ value, onChange }) => {
    const uploadPhoto = async (event: any) => {
        const base64Photo = await getOptimizedBase64FromInputFile(event);
        if (base64Photo) {
            onChange(base64Photo);
        }
        // Permite volver a elegir el mismo archivo
        event.target.value = '';
    };

    if (!value) {
        return (
            <Stack
                component="label"
                justifyContent="center"
                alignItems="center"
                spacing={1}
                width="100%"
                sx={{
                    aspectRatio: '1 / 1',
                    minHeight: 170,
                    border: '2px dashed #C3CDD6',
                    borderRadius: 3,
                    cursor: 'pointer',
                    color: 'text.secondary',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: '#2B2B2B', color: 'text.primary', bgcolor: '#FAFBFC' }
                }}
            >
                <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 42 }} />
                <Typography variant="body2" fontWeight={600}>
                    Subir foto
                </Typography>
                <Typography variant="caption" textAlign="center" px={1}>
                    Se optimiza automáticamente
                </Typography>
                <input type="file" hidden onChange={uploadPhoto} accept="image/*" />
            </Stack>
        );
    }

    return (
        <Stack spacing={1} width="100%">
            <Box
                component="img"
                src={value}
                alt="Foto del producto"
                width="100%"
                sx={{ aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 3, border: '1px solid #E3E8EE' }}
            />
            <Stack direction="row" spacing={1} justifyContent="center">
                <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<PhotoCameraOutlinedIcon />}
                >
                    Cambiar
                    <input type="file" hidden onChange={uploadPhoto} accept="image/*" />
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => onChange('')}
                >
                    Quitar
                </Button>
            </Stack>
        </Stack>
    );
};
